<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  inject,
  computed,
  watch,
  onMounted,
  defineEmits,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';
import {
  COMMENTS_SECTION_TITLE,
  COMMENT_CONVERSATION_TYPE,
  COMMENTS_EOF_MESSAGE,
  COMMENTS_EMPTY_LIST_MESSAGE,
} from 'dashboard/constants/comments';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import CloseDispositionModal from 'dashboard/components-next/ConversationWorkflow/CloseDispositionModal.vue';
import {
  needsContactClassification,
  isGreetingOnlyConversation,
  isFollowUpOutcome,
  GREETING_ONLY_REASON,
} from 'dashboard/helper/closeDisposition';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { debounce } from '@chatwoot/utils';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnRepliedByMeView,
  isOnCommentsView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import ConversationApi from 'dashboard/api/inbox/conversation';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const closeDispositionModalRef = ref(null);
const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showUnreadOnly = ref(false);
// Eltafouk perf: gate the watcher's heavy auto-paginate loop on a real
// user click. Without this the watcher fires on initial mount (HMR
// replay / double-mount during layout settle) and burns ~7.5s of
// backend round-trips before the agent has touched anything.
// Anyone who needs to programmatically flip showUnreadOnly to "on" and
// have it behave like a click MUST also set this flag to true.
const hasUserToggledUnread = ref(false);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// Eltafouk: the 3 assignee tabs (mine/unassigned/all) are meaningless inside the
// "Replied by me" (محادثات رديت عليها) view — the slice is already "mine", and
// their counts are the global account stats (never scoped to this folder), so
// they only mislead. Hide just the tabs for this view; the unread pill stays.
const isRepliedByMeView = computed(
  () => props.conversationType === 'last_replied_by_me'
);

// Eltafouk: single "are we in the Comments (التعليقات) section?" signal,
// provided once by ConversationView (the keystone). Drives the list title and
// hides the conversation-only assignee tabs here. Defaults to false so this
// component is safe if ever rendered without that provider.
const isCommentSection = inject(
  'isCommentSection',
  computed(() => false)
);

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count =
    assigneeTabItems.value.find(item => item.key === activeAssigneeTab.value)
      ?.count ?? 0;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    chatsOnView.value.length > 0;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

// Eltafouk: flag for the account-wide "جميع قنوات التعليقات" view.
const isAllCommentsView = computed(
  () =>
    isCommentSection.value &&
    !props.conversationInbox &&
    props.conversationType === COMMENT_CONVERSATION_TYPE
);

// Eltafouk: conversation types whose scope must survive BOTH the list query and
// the unread pill's /meta query, carried by the additive `unread` flag so the
// scope and the unread narrowing compose (ConversationFinder runs
// filter_by_conversation_type then filter_by_unread) instead of one replacing
// the other — and so `fetchUnreadMetaData` keeps the type rather than
// overriding it with 'unread'. 'mention' is deliberately absent: there "unread"
// means an unopened mention, counted from unreadMentionIds, and the finder's
// mention path never reaches filter_by_unread at all.
const SCOPED_UNREAD_CONVERSATION_TYPES = [
  'unattended',
  'last_replied_by_me',
  'participating',
];

const conversationFilters = computed(() => {
  // When the "غير مقروء" filter pill is pressed, route the request
  // through the backend's `conversation_type=unread` filter (a custom
  // Eltafouk scope on the Conversation model that mirrors the exact
  // criteria the sidebar's per-inbox unread badge uses: agent_last_seen
  // is null OR there's an incoming message newer than it). Without
  // this, a client-side `unread_count > 0` pass over the loaded slice
  // misses the unread row entirely on inboxes with tens of thousands of
  // conversations — and falling back to the broader `unattended` scope
  // drifted the displayed list away from the badge count.
  // Eltafouk: the all-comments view scopes to comment inboxes via
  // conversation_type='comments' — and the dashboard's list-builder getter ONLY
  // keeps comment conversations while that value is 'comments'. So we must NOT
  // overload conversation_type with 'unread' here (that empties the list). When
  // the "غير مقروء" pill is on we KEEP conversation_type='comments' and carry the
  // unread narrowing in a separate additive backend param `unread` (which layers
  // with_unread_incoming server-side, for BOTH the list index and the badge meta).
  // inboxId stays undefined for the all-comments view — the 'comments' scope
  // already narrows to comment inboxes on both sides; passing the id array would
  // break the getter's scalar inbox filter (Number([...]) === id → NaN).
  // Eltafouk: the additive `unread` overlay applies to every view whose
  // conversation_type already scopes the list server-side — the all-comments
  // view plus the types in SCOPED_UNREAD_CONVERSATION_TYPES. Those views used
  // to fall back to the client-side `unread_count > 0` pass alone, which only
  // sees the rows already loaded (1000 at a time), so on a large backlog the
  // list could show fewer rows than the pill counted. Narrowing server-side
  // keeps list and pill on one population by construction — the same guarantee
  // buildUnreadParams relies on below.
  const useServerUnreadScope =
    showUnreadOnly.value &&
    (isAllCommentsView.value ||
      SCOPED_UNREAD_CONVERSATION_TYPES.includes(props.conversationType));
  const effectiveConversationType =
    props.conversationType || (showUnreadOnly.value ? 'unread' : undefined);
  const effectiveInboxId = props.conversationInbox || undefined;
  // Eltafouk: when "غير مقروء" is on, ask the backend for the entire
  // unread set in one shot (capped server-side at 1000) instead of the
  // default 25-row page. For accounts whose unread total exceeds 1000
  // the remainder falls through to the standard pagination loop in the
  // showUnreadOnly watcher — those rows still get loaded, just in
  // additional 1000-row chunks rather than in the first response.
  const perPage = showUnreadOnly.value ? 1000 : undefined;
  return {
    inboxId: effectiveInboxId,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    perPage,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: effectiveConversationType,
    // Eltafouk: additive unread overlay — applies with_unread_incoming
    // server-side WITHOUT overloading conversation_type, so the scoping type
    // ('comments' client list-keep + backend scope, 'unattended', …) and the
    // unread narrowing coexist instead of one replacing the other.
    unread: useServerUnreadScope ? true : undefined,
  };
});

// Eltafouk: build the params for the unread badge's `conversationStats/getUnread`
// (/meta) call. In the account-wide comments view, scope the badge to comment
// inboxes via conversation_type='comments' + the additive `unread` flag — NOT an
// inbox-id array. The array is empty until inboxes finish loading on first paint,
// and an empty inbox_id is dropped from the request, briefly leaking the DM unread
// total into the badge. conversation_type='comments' scopes server-side with no
// client-loaded inbox list, so the count is correct from the very first request.
// (fetchUnreadMetaData keeps this conversation_type when `unread` is set instead
// of force-overriding it to 'unread'.) Every DM / single-inbox view is unchanged.
function buildUnreadParams(baseFilters = conversationFilters.value) {
  const params = { ...baseFilters, conversationType: undefined };
  if (isAllCommentsView.value) {
    params.conversationType = COMMENT_CONVERSATION_TYPE;
    params.unread = true;
  } else if (
    SCOPED_UNREAD_CONVERSATION_TYPES.includes(props.conversationType)
  ) {
    // Eltafouk: same treatment for the other scoping types. Blanking
    // conversationType made /meta answer with the whole inbox's unread total
    // while the list stayed scoped, so «غير مُعالجة» and «محادثات رديت عليها»
    // showed a pill reading e.g. 214 next to a 3-row list. The list query
    // carries the same pair, so both sides now count one population.
    params.conversationType = props.conversationType;
    params.unread = true;
  }
  return params;
}

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    // "المحادثات" is the DMs heading; show the comments heading instead when the
    // applied-filter view is a comment one.
    return isCommentSection.value
      ? COMMENTS_SECTION_TITLE
      : t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (props.conversationType === 'last_replied_by_me') {
    return t('SIDEBAR.REPLIED_BY_ME_CONVERSATIONS');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  // The "جميع قنوات التعليقات" view (conversationType='comments') has no inbox /
  // team / label / folder, so it would otherwise fall through to the DMs
  // heading "المحادثات". Show the comments heading instead.
  if (isCommentSection.value) {
    return COMMENTS_SECTION_TITLE;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  // Two-layer narrowing for the "غير مقروء" toggle:
  //   1. Backend layer (via conversationFilters → conversation_type=
  //      unattended) loads the unattended subset into the store, so
  //      conversations with unread messages are far more likely to be
  //      in the rendered slice than they would be in the unfiltered
  //      inbox (which can be tens of thousands deep).
  //   2. Local layer (here) narrows that subset to rows whose
  //      `unread_count > 0`. This matches the metric the badge counts
  //      via `inboxes/getUnattendedCount` — Conversation.unattended
  //      scope is broader (it also catches "never replied" rows with
  //      no new incoming activity), and showing those would make the
  //      list count drift away from the badge.
  if (showUnreadOnly.value) {
    if (props.conversationType === 'mention') {
      // On Mentions, "unread" means mentions the agent hasn't opened — filter
      // by the unread-mention list, not the conversation's unread messages
      // (which would wrongly hide mentions whose customer messages are read).
      const unreadIds = conversationStats.value?.unreadMentionIds || [];
      localConversationList = localConversationList.filter(conversation =>
        unreadIds.includes(conversation.id)
      );
    } else {
      localConversationList = localConversationList.filter(
        conversation => conversation.unread_count > 0
      );
    }
  }
  return localConversationList;
});

// Server-truth unread total for whatever scope the agent is viewing,
// Reads from conversationStats.unread* counts — the dedicated unread
// meta-slot fetched via `conversationStats/getUnread` whenever the
// view-defining filters change. That uses the EXACT same /meta endpoint
// the tabs read from (just with conversation_type=unread layered on),
// so the badge and the post-filter tab counts share one source of
// truth and can't drift apart anymore.
//
// Tab-aware: each tab gets the matching unread count. "محادثاتي" shows
// the agent's unread, "غير معيّن" shows unassigned unread, "الكل" shows
// the full unread total. Mirrors how the unread filter itself narrows
// by assignee_type.
const unreadCountInCurrentView = computed(() => {
  const stats = conversationStats.value || {};
  // On the Mentions view "unread" means mentions the agent hasn't opened —
  // the unread-message filter can't combine with the mention scope, so use the
  // unread-mention list straight from the meta.
  if (props.conversationType === 'mention') {
    return (stats.unreadMentionIds || []).length;
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return stats.unreadMineCount || 0;
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return stats.unreadUnAssignedCount || 0;
  }
  return stats.unreadAllCount || 0;
});

// Eltafouk: unread count for the active custom-view folder.
//
// The old approach pulled the `inbox_id` clauses out of the saved query and
// summed each inbox's full unattended count. That silently dropped every
// NON-inbox clause in the folder filter (e.g. `assignee_type is_not_present`
// for "غير معيّن", or the "no reply" condition for "بدون رد"), because the
// /inboxes/unattended_counts endpoint groups by inbox_id only — with no
// assignee/reply-status narrowing. So those narrow folders showed the whole
// inbox's unattended total instead of their actual (much smaller) subset.
//
// Fix: ask the backend for a real count over the SAME saved filter the list
// itself uses. POST /conversations/filter with the folder's query payload +
// `conversation_type=unread` runs the full FilterService scope (every clause,
// plus the permission filter) and returns `meta.all_count` — the exact unread
// total for this folder. We request a single-row page since we only need the
// count, never the rows.
const folderUnreadCount = ref(0);

let folderUnreadRequestToken = 0;
async function fetchFolderUnreadCount() {
  if (!hasActiveFolders.value) {
    folderUnreadCount.value = 0;
    return;
  }
  const query = activeFolder.value?.query;
  if (!query) {
    folderUnreadCount.value = 0;
    return;
  }
  // Guard against stale responses landing after a folder switch.
  folderUnreadRequestToken += 1;
  const token = folderUnreadRequestToken;
  try {
    const { data } = await ConversationApi.filter({
      queryData: query,
      page: 1,
      perPage: 1,
      conversationType: 'unread',
    });
    if (token !== folderUnreadRequestToken) return;
    folderUnreadCount.value = data?.meta?.all_count || 0;
  } catch (error) {
    if (token !== folderUnreadRequestToken) return;
    folderUnreadCount.value = 0;
  }
}

// Eltafouk: websocket-driven refreshes of this pill must be debounced. The
// token guard above only discards stale *responses* — every call still issues a
// POST /conversations/filter, and `fetch_conversation_stats` is emitted from
// seven actionCable handlers (once per inbound message). Together with the
// sidebar's «محتاج-رد» badge this produced two uncapped requests per event
// against the heaviest endpoint in the app. Navigation-triggered refreshes
// (mount seed, activeFolder watcher) keep calling the raw function so switching
// folders still updates the pill immediately.
const debouncedFetchFolderUnreadCount = debounce(
  fetchFolderUnreadCount,
  10000,
  false,
  20000
);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload, extra = {}) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  const dispatched = store.dispatch('fetchFilteredConversations', {
    queryData: filterQueryGenerator(payload),
    page,
    // Eltafouk: when the "غير مقروء" pill is on, the watcher passes
    // perPage:1000 + conversationType:'unread' so the filter endpoint
    // returns the whole unread set in one shot (same behaviour as the
    // index endpoint). Unset on regular filter use → backend default.
    perPage: extra.perPage,
    conversationType: extra.conversationType,
  });

  showAdvancedFilters.value = false;
  return dispatched.then(emitConversationLoaded);
}

function fetchSavedFilteredConversations(payload, extra = {}) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      perPage: extra.perPage,
      conversationType: extra.conversationType,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

// Dedup guard for loadMoreConversations: the page most recently requested via
// infinite scroll. Reset to 0 on every view switch (resetAndFetchData /
// updateAssigneeTab) so a stale value never silently drops the first scroll
// fetch in the new view. Declared here so the reset sites above can see it.
let lastRequestedPage = 0;

// Eltafouk: drives the «غير مقروء» loading affordance (skeleton + a steady
// "loading more" indicator) for the duration of the toggle-ON auto-paginate
// loop, so the list doesn't flash «no conversations» or blink the spinner once
// per page. Strictly ADDITIVE to chatListLoading; cleared on every loop-exit
// path and in the toggle-off / mention arms, so it can never leak `true`.
const unreadLoopActive = ref(false);
// Eltafouk: scroll-position preservation across the «غير مقروء» toggle. We
// snapshot the agent's place in the FULL list on toggle-ON (before it's
// emptied) and restore it once the toggle-OFF refetch repopulates, so turning
// the filter off doesn't snap them to the top. preFilterScrollTop is a raw
// $el.scrollTop; pendingScrollRestore gates the one-shot restore watcher below.
let preFilterScrollTop = 0;
let pendingScrollRestore = false;

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  lastRequestedPage = 0;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  // Eltafouk: clear the shared unread badge slot synchronously so the previous
  // view's count never leaks into this newly-opened view (esp. التعليقات ↔
  // المحادثات, which reuse the same ChatList instance). The re-scoped getUnread
  // below refills it for THIS view.
  store.dispatch('conversationStats/resetUnread');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  // Re-scope the shared tab-count stats to THIS view right away. The /meta
  // fetch is debounced and module-shared, so a stale call queued by the
  // previous view (e.g. All Conversations' whole-inbox totals) could fire
  // later and overwrite this view's counters — that's why switching
  // All ↔ Mentions briefly showed the inbox-wide numbers on Mentions.
  store.dispatch('conversationStats/get', conversationFilters.value);
  // Re-scope the unread badge too. resetAndFetchData runs on EVERY navigation
  // (inbox / label / team / conversationType watchers), so dispatching getUnread
  // here is what refreshes the badge when switching sections — previously only
  // the tab-count `get` above re-ran, leaving the unread slot showing the prior
  // view's number (the cross-section leak).
  store.dispatch('conversationStats/getUnread', buildUnreadParams());
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }
  // Eltafouk: the IntersectionObserver can fire several times within a
  // single tick (during layout/scroll bursts) before the dispatched
  // fetch flips chatListLoading=true on the next microtask. Without
  // this guard each fire queues a duplicate page request that the
  // backend dutifully processes — wasted seconds on top of the
  // legitimate fetch.
  const nextPage = currentFiltersPage.value + 1;
  if (nextPage === lastRequestedPage) return;
  lastRequestedPage = nextPage;

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  const scrollerEl = scroller?.$el;
  if (scrollerEl && scrollerEl.scrollHeight > scrollerEl.clientHeight) {
    const { scrollTop, scrollHeight, clientHeight } = scrollerEl;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    store.dispatch('emptyAllConversations');
    store.dispatch('conversationPage/reset');
    lastRequestedPage = 0;
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    fetchConversations();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  } else if (isOnRepliedByMeView({ route: { name } })) {
    conversationType = 'last_replied_by_me';
  } else if (isOnCommentsView({ route: { name } })) {
    // Eltafouk: «جميع قنوات التعليقات» carries no inbox_id / label / team / folder
    // id in its route, so without this branch conversationType stayed '' and
    // conversationListPageURL fell through to the DM dashboard — every
    // mark-as-unread (and delete) threw the agent out of the comments section
    // and lost the queue, the scroll position and the «غير مقروء» pill.
    conversationType = COMMENT_CONVERSATION_TYPE;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

// Eltafouk: reflect the agent's own mark-as-unread in the «غير مقروء» pill
// immediately. The pill's only source is the /meta call behind a 500ms debounce
// whose 10s max-wait ceiling actually binds during working hours (the events
// that drive it never leave a 500ms gap), plus a 5s server cache — so the
// agent's +1 took up to ~15s to appear, by which time colleagues reading
// conversations had subtracted more than she added and the number went DOWN.
// This costs no extra request; the next /meta corrects any drift.
function adjustUnreadPill(conversation, delta) {
  if (!conversation) return;
  // Folder views read a separate server-counted ref instead of the stats store.
  if (hasActiveFolders.value) {
    folderUnreadCount.value = Math.max(0, folderUnreadCount.value + delta);
    return;
  }
  const assigneeId = conversation.meta?.assignee?.id;
  store.dispatch('conversationStats/adjustUnread', {
    delta,
    mine: !!assigneeId && assigneeId === currentUser.value?.id,
    unassigned: !assigneeId,
  });
}

async function markAsUnread(conversationId) {
  // Read the pre-action state: markMessagesUnread commits unread_count = 1, so
  // after the await there is no way to tell whether the row was already counted.
  const conversation = getConversationById.value(conversationId);
  const wasAlreadyUnread = (conversation?.unread_count || 0) > 0;
  try {
    const unreadCount = await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    // Only move the pill when the SERVER actually flipped the row. #unread
    // answers 200 without changing anything on a conversation with no incoming
    // message (the outgoing-only «تم»→DM population), and the menu item is
    // still offered there — assuming success would add a +1 that the next
    // /meta silently takes away again.
    if (!wasAlreadyUnread && unreadCount > 0) adjustUnreadPill(conversation, 1);
    // Eltafouk: the redirect exists only so that marking the conversation the
    // agent is CURRENTLY READING unread isn't instantly undone by the
    // read-on-open path. It used to run unconditionally, so right-clicking a
    // different card in the list closed the open conversation and threw away
    // its scroll position for no reason.
    //
    // Compared against the store's selected chat id, NOT the route param: the
    // conversation routes are split between `:conversationId` (6 of them) and
    // `:conversation_id` (4, including the main `accounts/:accountId/
    // conversations/:conversation_id`), so a route-param check would silently
    // never match on the most-used view and the open conversation would go
    // back to read the moment it was marked unread. `selectedChatId` is set
    // from the conversation's own numeric id in SET_CURRENT_CHAT_WINDOW.
    if (conversationId === store.state.conversations.selectedChatId) {
      redirectToConversationList();
    }
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  // Same reasoning as markAsUnread, mirrored: take the count off the pill as
  // soon as the server confirms, instead of leaving it inflated for up to ~15s.
  const conversation = getConversationById.value(conversationId);
  const wasUnread = (conversation?.unread_count || 0) > 0;
  try {
    const unreadCount = await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
    // Same rule as markAsUnread. update_last_seen refuses the read stamp on
    // comment conversations and answers 200 with the row untouched; #173 hid
    // «تحديد كمقروء» on the comments SECTION, but `isCommentSection` is
    // route-based, so a comment conversation reached through a label or folder
    // view still offers it. Requiring the server to report 0 keeps the pill
    // honest in exactly that case.
    if (wasUnread && unreadCount === 0) adjustUnreadPill(conversation, -1);
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null,
  closeDisposition = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }
  if (closeDisposition) {
    payload.closeDisposition = closeDisposition;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

// The upstream resolve flow (required-attributes gate then resolve), carrying the
// close-disposition answers with it.
function proceedResolveFromList(
  conversationId,
  snoozedUntil,
  closeDisposition
) {
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
      closeDisposition,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(
      conversationId,
      wootConstants.STATUS_TYPE.RESOLVED,
      snoozedUntil,
      null,
      closeDisposition
    );
  }
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Eltafouk: ask the close-disposition questions first; proceedResolveFromList
  // continues with the upstream required-attributes gate after they are answered.
  // Greeting-only threads (safe only when the full tiny thread is in the store)
  // skip the questions and get stamped «ترحيب فقط», same as the header path.
  const conversation = getConversationById.value(conversationId);
  if (isGreetingOnlyConversation(conversation)) {
    proceedResolveFromList(conversationId, snoozedUntil, {
      reason: GREETING_ONLY_REASON,
      note: null,
      outcome: null,
      classification: null,
    });
    return;
  }
  closeDispositionModalRef.value?.open(
    { id: conversationId, snoozedUntil },
    { askClassification: needsContactClassification(conversation) }
  );
}

function handleCloseDispositionFromList({
  reason,
  note,
  outcome,
  classification,
  snoozedUntil,
  context,
}) {
  if (!context) return;
  const closeDisposition = { reason, note, outcome, classification };

  // Same branch as the header resolve button: «محتاجة متابعة» snoozes to the time the
  // agent picked instead of resolving, so the follow-up comes back on its own.
  if (isFollowUpOutcome(outcome) && snoozedUntil) {
    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.SNOOZED,
      snoozedUntil,
      null,
      closeDisposition
    );
    return;
  }

  proceedResolveFromList(context.id, context.snoozedUntil, closeDisposition);
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes,
      context.closeDisposition || null
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) {
    // The generic tab/unread stats are scoped to the inbox/tab view, not to
    // a folder's saved filter — so we skip them here. But the folder-aware
    // unread pill still needs to track live changes; refresh it against the
    // folder query so the count doesn't go stale as messages arrive / are read.
    if (hasActiveFolders.value) {
      debouncedFetchFolderUnreadCount();
    }
    return;
  }
  store.dispatch('conversationStats/get', conversationFilters.value);
  // Refresh the unread companion stats so the badge stays in sync with
  // the post-filter tab counts. Stripping showUnreadOnly off the params
  // is intentional — `getUnread` always forces conversation_type=unread,
  // and we don't want our own showUnreadOnly toggle to confuse it.
  // buildUnreadParams additionally injects the comment-inbox ids in the
  // account-wide comments view so the badge isn't scoped to DM inboxes.
  store.dispatch('conversationStats/getUnread', buildUnreadParams());
});

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  // Seed the unread badge's count source. Fires on every list mount so
  // the pill has a real number on first render rather than 0.
  store.dispatch('conversationStats/getUnread', buildUnreadParams());
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
    // Seed the folder-aware unread pill with a real server count.
    fetchFolderUnreadCount();
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// Eltafouk: the "غير مقروء" pill is a view-local narrowing, not a global
// preference — clear it on any navigation (inbox / label / type / team / folder)
// so a new view never opens silently filtered to unread-only. Reset
// hasUserToggledUnread first so flipping showUnreadOnly off here doesn't
// trip its watcher's heavy unread-loop; the navigation watcher's own
// resetAndFetchData handles the refetch.
function resetUnreadFilterOnNavigation() {
  hasUserToggledUnread.value = false;
  showUnreadOnly.value = false;
  // Cancel any in-flight unread-loop affordance + pending scroll restore so they
  // never bleed into the view we're navigating to (the new view's own
  // resetAndFetchData owns its loading state and scroll).
  unreadLoopActive.value = false;
  pendingScrollRestore = false;
}

// Eltafouk: comment views and the "replied by me" view both hide the assignee
// tabs, so a stale 'me'/'unassigned' selection carried over from a DM view would
// silently over-filter the list with no visible tab to reset it (this is the
// "محادثاتي shows a conversation while I'm in Comments" report). Pin the scope
// back to ALL whenever we navigate into one of these tab-less views.
function pinAssigneeTabForTablessViews() {
  if (isRepliedByMeView.value || isCommentSection.value) {
    activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  }
}

watch(activeTeam, () => {
  resetUnreadFilterOnNavigation();
  resetAndFetchData();
});

watch(
  computed(() => props.conversationInbox),
  () => {
    resetUnreadFilterOnNavigation();
    pinAssigneeTabForTablessViews();
    resetAndFetchData();
  }
);
watch(
  computed(() => props.label),
  () => {
    resetUnreadFilterOnNavigation();
    pinAssigneeTabForTablessViews();
    resetAndFetchData();
  }
);
watch(
  computed(() => props.conversationType),
  () => {
    resetUnreadFilterOnNavigation();
    // Pin the assignee scope back to ALL on entry to a tab-less view (replied-by-me
    // or a comment view) so a stale selection can't silently over-filter the list.
    pinAssigneeTabForTablessViews();
    resetAndFetchData();
  }
);
// Eltafouk: toggling the "غير مقروء" pill changes `conversationFilters`
// (it injects conversation_type=unread), but the existing
// conversationFilters watcher below only commits the new filter to the
// store — it doesn't refetch. Without a reset+refetch, the 800-row
// list that was loaded for the unfiltered view stays on screen and
// completely buries the rows the backend would return.
// Treat showUnreadOnly the same way as the other view-defining props
// (inbox/team/label/conversationType): clear the list and refetch on
// either edge so the displayed set always matches the filter.
//
// Also refresh per-inbox unattended counts so the badge number (which
// is summed from those counts) doesn't lag behind the live meta result
// that drives the tab counters — agents reported a 17-vs-18 drift when
// the cached sidebar values were stale.
watch(showUnreadOnly, async newVal => {
  // Skip the heavy unread-loop on mount-time / HMR-replay firings.
  // Only proceed when the value change traces back to a real click on
  // either of the two toggle buttons (which set hasUserToggledUnread).
  if (!hasUserToggledUnread.value) return;

  store.dispatch('inboxes/fetchUnattendedCounts');

  if (!newVal) {
    // Toggle OFF: clear the loading affordance and flag a one-shot scroll
    // restore so the full-list refetch returns the agent near where they were
    // (handled by the conversationList-length watcher below) instead of top.
    unreadLoopActive.value = false;
    pendingScrollRestore = true;
    resetAndFetchData();
    return;
  }

  // Mentions view: the full mention list is already loaded and the unread
  // toggle is a pure client-side narrowing (see conversationList). Just refresh
  // the mentions to get fresh read/unread state — skip the heavy unread
  // auto-paginate loop, which targets large inbox/unread scopes.
  if (props.conversationType === 'mention') {
    unreadLoopActive.value = false;
    resetAndFetchData();
    return;
  }

  // Eltafouk: snapshot the agent's place in the FULL list BEFORE we empty it,
  // so toggling the pill OFF can return them near it instead of jumping to top.
  preFilterScrollTop = conversationDynamicScroller.value?.$el?.scrollTop ?? 0;
  pendingScrollRestore = false;
  // Drive the skeleton + steady "loading more" indicator for the whole loop.
  // Cleared right after emitConversationLoaded below (every loop exit funnels
  // there) and in the toggle-off / mention arms above, so it can't leak `true`.
  unreadLoopActive.value = true;

  // Eltafouk: when "غير مقروء" is toggled ON, auto-paginate until
  // every unread conversation in the current scope is loaded. Without
  // this the agent would have to scroll through ~20 pages of 25 each
  // to reach the full unread set. Hard-capped at 50 pages (1,250 rows)
  // so a runaway never floods the backend; the virtual scroller
  // handles that many rows fine.
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');

  // Eltafouk: `emptyAllConversations` clears the store synchronously,
  // but `chatsOnView` is driven by a `watch(chatLists, …)` that flushes
  // on the next tick — without this await, the loop's `lenBefore`
  // would still reflect the residual list (~25 rows from the initial
  // folder load), making `added = N - 25 < perPage` trigger the
  // tail-detect short-circuit one page too early and clip ~25 unread
  // rows off the displayed total.
  await nextTick();

  // Eltafouk: pick the right fetch path for the current view. Folder /
  // custom-view routes (e.g. "جميع قنوات التعليقات", saved filters)
  // go through `POST /conversations/filter` with the saved query +
  // `conversation_type=unread` overlay; ad-hoc applied filters do the
  // same but with the user's filter payload; the plain conversation
  // list uses `GET /conversations` via `fetchAllConversations`. All
  // three honour `per_page` server-side (capped at 1000).
  //
  // Eltafouk: the comments folder ("جميع قنوات التعليقات") trips on
  // the jbuilder partial cost — 619 unread rows × the slim partial
  // costs ~1.5s of Views time, plus the Egypt→edge round-trip on a
  // single 2.7 MB response. Asking for smaller pages sequentially
  // makes the FIRST batch land in ~700-900 ms instead of 3+ seconds:
  // the user sees real rows immediately and the rest stream in behind
  // while they're reading the top of the list. The plain index path
  // (`fetchAllConversations`) stays on 1000 — its rows-per-second is
  // ~3x faster (lighter partial, no permission-filter overhead) so
  // chunking buys nothing there.
  const folderQuery = hasActiveFolders.value ? activeFolder.value.query : null;
  const isFilterPath = Boolean(folderQuery) || hasAppliedFilters.value;
  const requestedPerPage = isFilterPath ? 250 : 1000;

  // Eltafouk #93: capture the scope this loop was started for. The fetch
  // path (`folderQuery`) and the pagination key are bound to the folder
  // that was active when the toggle was pressed. If the agent switches
  // folder (or otherwise navigates) mid-loop, `props.foldersId` changes —
  // and any in-flight iteration that resolves afterwards would (a) write
  // a stale-query response into the now-current view's list and (b)
  // dispatch `setEndReached` against the new view's pagination key,
  // corrupting its scroll state. Re-check this token after every await
  // and bail without mutating pagination if it no longer matches.
  // (Navigation also clears `showUnreadOnly` via
  // resetUnreadFilterOnNavigation, and an explicit pill-off does too, so
  // the toggle itself is the second guard.)
  const scopeToken = props.foldersId;
  const isStillCurrentScope = () =>
    showUnreadOnly.value && props.foldersId === scopeToken;

  let safety = 50;
  while (
    safety > 0 &&
    isStillCurrentScope() &&
    !hasCurrentPageEndReached.value
  ) {
    safety -= 1;
    const lenBefore = chatsOnView.value.length;
    store.dispatch('updateChatListFilters', conversationFilters.value);
    if (folderQuery) {
      // eslint-disable-next-line no-await-in-loop
      await fetchSavedFilteredConversations(folderQuery, {
        perPage: requestedPerPage,
        conversationType: 'unread',
      });
    } else if (hasAppliedFilters.value) {
      // eslint-disable-next-line no-await-in-loop
      await fetchFilteredConversations(appliedFilters.value, {
        perPage: requestedPerPage,
        conversationType: 'unread',
      });
    } else {
      // eslint-disable-next-line no-await-in-loop
      await store.dispatch('fetchAllConversations');
    }
    // eslint-disable-next-line no-await-in-loop
    await nextTick();
    // Eltafouk #93: the toggle/folder may have changed while the fetch was
    // in flight. Bail before touching shared pagination state so we never
    // call `setEndReached` against (or short-circuit) a scope this loop no
    // longer owns. The navigation watcher's own resetAndFetchData already
    // owns the refetch for the new view.
    if (!isStillCurrentScope()) {
      break;
    }
    const added = chatsOnView.value.length - lenBefore;
    // The backend runs the same expensive scope+sort even when the
    // page is empty, so a probe request to confirm "no more rows" costs
    // as much as a real data fetch (~6s on the prod-sized schema).
    // Detect the tail of the unread set straight from the response size:
    // a page that didn't fill `per_page=1000` is the last one. On the
    // filter path `chatsOnView` is fed by `chatLists.value` (which the
    // server replaces wholesale on each page), so `added` is the new
    // rows that arrived this iteration — same semantics as the index
    // path's append-only behaviour for tail-detection purposes.
    if (added < requestedPerPage) {
      // CRITICAL: tell the pagination store we hit the tail, otherwise the
      // virtual scroller's IntersectionObserver fires loadMoreConversations()
      // a few seconds after first paint and burns another 5-second backend
      // round-trip producing zero new rows. `actionHelpers#setPageFilter`
      // only flips this flag when the server returns an empty page, which
      // never happens on our short-circuit path.
      store.dispatch('conversationPage/setEndReached', {
        filter: currentPageFilterKey.value,
      });
      break;
    }
  }
  emitConversationLoaded();
  // Every loop exit (both `break`s and the natural end) funnels here — clear
  // the affordance flag so the skeleton / steady spinner never linger.
  unreadLoopActive.value = false;
});

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetUnreadFilterOnNavigation();
  resetAndFetchData();
  // Refresh the folder-aware unread pill against the new folder's saved
  // filter (clears to 0 when navigating away from any folder).
  fetchFolderUnreadCount();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

// Eltafouk: one-shot scroll restore after a «غير مقروء» toggle-OFF refetch.
// Once the full list has repopulated (length > 0) and a restore is pending,
// return the agent near their pre-filter position. The offset is clamped above
// the infinite-scroll trigger so the restore never fires an extra page fetch on
// the busy box; exact-pixel restore isn't achievable on a keyless, variable-
// height virtual scroller, so this is a best-effort "don't snap to the top".
watch(
  () => conversationList.value.length,
  len => {
    if (!pendingScrollRestore || len === 0) return;
    pendingScrollRestore = false;
    requestAnimationFrame(() => {
      const el = conversationDynamicScroller.value?.$el;
      if (!el) return;
      const maxSafe = Math.max(0, el.scrollHeight - el.clientHeight - 120);
      el.scrollTop = Math.min(preFilterScrollTop, maxSafe);
    });
  }
);

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
    // Eltafouk: skip the parallel /meta refresh when the only thing
    // that changed is the showUnreadOnly toggle (or our injected
    // per_page bump). The showUnreadOnly watcher already kicks off
    // fetchAllConversations + an inboxes/unattended_counts refresh —
    // adding a third concurrent /meta call burns ~300-500ms of Postgres
    // contention without surfacing any number the user actually sees
    // until the main fetch returns. Only refresh getUnread on changes
    // that genuinely shift the badge scope (inbox / assignee / status /
    // label / team).
    const scopeShifted =
      newVal.inboxId !== oldVal?.inboxId ||
      newVal.assigneeType !== oldVal?.assigneeType ||
      newVal.status !== oldVal?.status ||
      newVal.teamId !== oldVal?.teamId ||
      JSON.stringify(newVal.labels) !== JSON.stringify(oldVal?.labels);
    if (scopeShifted) {
      // Keep the unread badge in lockstep with the post-filter tab
      // counts: same /meta endpoint, same scope params, the only delta
      // is `conversation_type=unread`. Strip our own showUnreadOnly
      // injection (conversationType: 'unread') so getUnread is always
      // the canonical unread-meta call regardless of whether the agent
      // currently has the filter pill pressed. buildUnreadParams also
      // injects the comment-inbox ids in the account-wide comments view.
      store.dispatch('conversationStats/getUnread', buildUnreadParams(newVal));
    }
  }
});
</script>

<template>
  <!-- Eltafouk: عرض عمود المحادثات +58px على البريكبوينتين (كان 340 / 412).
       الزيادة دي ثمن شريحة التصنيف وعلامة «عميل مسجّل» اللي بقت قبل الاسم على
       كل كارت. من غيرها الاسم كان بينهرس: على عمود 340 نزل من ~110px لـ~41px —
       يعني حرفين تلاتة بس («…يم» بدل «ريهام مصطفي نسيم»). بعد الزيادة، على 2xl
       مفيش أي قص خالص — الأسماء بنفس عرضها قبل الشريحة بالظبط — وعلى شاشة 1366
       الاسم رجع ~100px. -->
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[398px] 2xl:w-[470px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :inbox="inbox"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :folder-unread-count="folderUnreadCount"
      :folder-unread-active="showUnreadOnly"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @toggle-folder-unread="
        hasUserToggledUnread = true;
        showUnreadOnly = !showUnreadOnly;
      "
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <div
      v-if="!hasAppliedFiltersOrActiveFolders"
      class="flex items-center gap-2 px-3"
    >
      <ChatTypeTabs
        v-if="!isRepliedByMeView && !isCommentSection"
        :items="assigneeTabItems"
        :active-tab="activeAssigneeTab"
        is-compact
        class="flex-1"
        @chat-tab-change="updateAssigneeTab"
      />
      <!-- Eltafouk: elevated unread filter. Badge shows the true
           server-side unread total for the current scope (matches the
           count the sidebar displays on the same inbox), so the agent
           sees the real backlog size — not just the slice currently
           rendered in the virtual list. The count badge is a vivid red
           pulsing chip when there are unread threads, to call attention
           without bouncing the layout. -->
      <button
        type="button"
        class="group/unread relative inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-full text-[10px] font-semibold transition-all duration-200 ease-out ring-1 active:scale-[0.97]"
        :class="[
          showUnreadOnly
            ? 'bg-n-slate-12 text-white ring-n-slate-12 shadow-[0_2px_8px_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.18)] ps-2 pe-1.5'
            : 'bg-white text-n-slate-12 ring-n-alpha-2 hover:ring-n-slate-7 hover:bg-n-alpha-1 shadow-sm ps-2 pe-1.5',
          unreadCountInCurrentView === 0 ? 'pe-2' : '',
        ]"
        @click="
          hasUserToggledUnread = true;
          showUnreadOnly = !showUnreadOnly;
        "
      >
        <fluent-icon icon="mail-unread" size="10" />
        <span>{{ $t('CHAT_LIST.UNREAD') }}</span>
        <!-- Badge sits left-of-text in RTL. width is content-driven with a
             generous min so 3-digit counts (the common case here) never
             clip into the parent's rounded-full curve. -->
        <span
          v-if="unreadCountInCurrentView > 0"
          class="inline-flex h-[18px] min-w-[26px] items-center justify-center rounded-full bg-[#DC2626] px-1.5 text-[10px] font-bold leading-none tabular-nums text-white animate-unread-glow ring-1 ring-inset ring-white/10"
        >
          {{ unreadCountInCurrentView }}
        </span>
      </button>
    </div>

    <p
      v-if="!chatListLoading && !conversationList.length && !unreadLoopActive"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{
        isCommentSection
          ? COMMENTS_EMPTY_LIST_MESSAGE
          : $t('CHAT_LIST.LIST.404')
      }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="overflow-hidden relative flex-1 conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <!-- Eltafouk: skeleton placeholder shown only while the «غير مقروء»
           auto-paginate loop is loading its FIRST page (list still empty), so
           the pill click doesn't flash «no conversations» / a blank pane. Pure
           presentation; disappears the instant real rows arrive. -->
      <div
        v-if="unreadLoopActive && !conversationList.length"
        class="overflow-hidden absolute inset-0 z-10 bg-n-surface-1"
      >
        <div
          v-for="n in 8"
          :key="n"
          class="flex gap-3 items-center px-4 h-[72px] border-b border-n-weak"
        >
          <div class="w-10 h-10 rounded-full animate-pulse bg-n-alpha-2" />
          <div class="flex-1 space-y-2">
            <div class="w-1/3 h-3 rounded animate-pulse bg-n-alpha-2" />
            <div class="w-2/3 h-3 rounded animate-pulse bg-n-alpha-1" />
          </div>
        </div>
      </div>
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="overflow-auto w-full h-full"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.labels?.length,
              item.custom_attributes?.comment_deleted_at,
              item.custom_attributes?.comment_hidden_at,
              item.additional_attributes?.call_status,
            ]"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div
            v-if="chatListLoading || unreadLoopActive"
            class="flex justify-center my-4"
          >
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ isCommentSection ? COMMENTS_EOF_MESSAGE : $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <CloseDispositionModal
      ref="closeDispositionModalRef"
      @submit="handleCloseDispositionFromList"
    />
  </div>
</template>
